import { BuildingStatus, type BuildingInfo } from '@/gen/building/v1/building_pb'

export type PropertySortKey = 'name' | 'type' | 'tier' | 'status' | 'income' | 'collect' | 'health'
export type SortDir = 'asc' | 'desc'

/** Status order for sorting: what needs attention first. */
const STATUS_RANK: Record<BuildingStatus, number> = {
  [BuildingStatus.CHALLENGED]: 0,
  [BuildingStatus.DESTROYED]: 1,
  [BuildingStatus.CLAIMED]: 2,
  [BuildingStatus.UNCLAIMED]: 3,
  [BuildingStatus.UNSPECIFIED]: 4,
}

/** Collect time: unset ("not collectable") always sorts last, whatever the direction. */
function collectValue(b: BuildingInfo): number | null {
  return b.collectReadyAtUnixMs === undefined ? null : Number(b.collectReadyAtUnixMs)
}

function compare(a: BuildingInfo, b: BuildingInfo, key: PropertySortKey): number {
  switch (key) {
    case 'name':
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    case 'type':
      return a.gameType.localeCompare(b.gameType) || a.name.localeCompare(b.name)
    case 'tier':
      return a.tier - b.tier
    case 'status':
      return STATUS_RANK[a.status] - STATUS_RANK[b.status]
    case 'income':
      return Number(a.tierIncome - b.tierIncome)
    case 'health':
      return a.health - b.health
    case 'collect':
      return (collectValue(a) ?? 0) - (collectValue(b) ?? 0)
  }
}

/** Stable sort; ties keep name order. Pure, so the table and the tests agree. */
export function sortBuildings(rows: readonly BuildingInfo[], key: PropertySortKey, dir: SortDir): BuildingInfo[] {
  const sign = dir === 'asc' ? 1 : -1
  return rows
    .map((b, i) => ({ b, i }))
    .sort((x, y) => {
      if (key === 'collect') {
        const cx = collectValue(x.b)
        const cy = collectValue(y.b)
        if (cx === null && cy !== null) return 1
        if (cy === null && cx !== null) return -1
      }
      return sign * compare(x.b, y.b, key) || x.b.name.localeCompare(y.b.name) || x.i - y.i
    })
    .map((x) => x.b)
}
